import { Facebook, Instagram, MessageCircle, Youtube } from "lucide-react";
import { SITE } from "@/config/site";
import { scrollToId, scrollToTop } from "@/lib/scroll";
import { trackEvent, trackOutbound, withUtm } from "@/lib/analytics";

const EXPLORE = [
  ["Experience", "#experience"],
  ["Highlights", "#highlights"],
  ["Artists", "#artists"],
  ["Details", "#details"],
  ["Tickets", "#tickets"],
  ["Venue", "#venue"],
  ["FAQ", "#faq"],
];

export function Footer() {
  return (
    <footer
      data-testid="site-footer"
      className="border-t border-[#D4AF37]/15 bg-[#0A0805] px-5 pb-32 pt-16 sm:px-8 md:pb-16"
    >
      <div className="mx-auto max-w-6xl">
        <div className="grid grid-cols-1 gap-12 sm:grid-cols-2 lg:grid-cols-4">
          <div>
            <button
              type="button"
              data-testid="footer-brand-link"
              onClick={scrollToTop}
              className="flex items-center text-left"
            >
              <img
                src={SITE.brand.logo}
                alt={SITE.brand.logoAlt}
                data-testid="footer-logo"
                className="h-20 w-auto"
              />
            </button>
            <p className="mt-5 text-sm leading-relaxed text-[#918A7E] sm:text-base">
              {SITE.tagline}.
              <br />
              {SITE.date} · {SITE.venue.area}, {SITE.city}.
            </p>
            <p className="mt-4 text-sm leading-relaxed text-[#C8C2B7]">
              Event by <span className="font-semibold text-[#F3E5AB]">{SITE.organizer}</span>
            </p>
          </div>

          <nav aria-label="Explore">
            <p className="text-xs font-bold uppercase tracking-[0.28em] text-[#D4AF37]">Explore</p>
            <ul className="mt-5 space-y-3">
              {EXPLORE.map(([label, id]) => (
                <li key={id}>
                  <a
                    href={id}
                    data-testid={`footer-${label.toLowerCase()}-link`}
                    onClick={(e) => {
                      e.preventDefault();
                      scrollToId(id);
                    }}
                    className="text-sm text-[#C8C2B7] transition-colors hover:text-[#E6CA65] sm:text-base"
                  >
                    {label}
                  </a>
                </li>
              ))}
            </ul>
          </nav>

          <div>
            <p className="text-xs font-bold uppercase tracking-[0.28em] text-[#D4AF37]">Connect</p>
            <ul className="mt-5 space-y-3">
              <li>
                <a
                  href={withUtm(SITE.instagramUrl)}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="footer-instagram-link"
                  onClick={() => {
                    trackEvent("instagram_click");
                    trackOutbound("instagram");
                  }}
                  className="inline-flex items-center gap-2 text-sm text-[#C8C2B7] transition-colors hover:text-[#E6CA65] sm:text-base"
                >
                  <Instagram className="h-4 w-4" aria-hidden="true" /> Follow us on Instagram
                </a>
              </li>
              <li>
                <a
                  href={withUtm(SITE.facebookUrl)}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="footer-facebook-link"
                  onClick={() => {
                    trackEvent("facebook_click");
                    trackOutbound("facebook");
                  }}
                  className="inline-flex items-center gap-2 text-sm text-[#C8C2B7] transition-colors hover:text-[#E6CA65] sm:text-base"
                >
                  <Facebook className="h-4 w-4" aria-hidden="true" /> Follow us on Facebook
                </a>
              </li>
              <li>
                <a
                  href={withUtm(SITE.whatsappChannelUrl)}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="footer-whatsapp-channel-link"
                  onClick={() => {
                    trackEvent("whatsapp_click");
                    trackOutbound("whatsapp-channel");
                  }}
                  className="inline-flex items-center gap-2 text-sm text-[#C8C2B7] transition-colors hover:text-[#E6CA65] sm:text-base"
                >
                  <MessageCircle className="h-4 w-4" aria-hidden="true" /> Join our WhatsApp Channel
                </a>
              </li>
              {SITE.youtubeUrl && (
                <li>
                  <a
                    href={withUtm(SITE.youtubeUrl)}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="footer-youtube-link"
                    onClick={() => {
                      trackEvent("youtube_click");
                      trackOutbound("youtube");
                    }}
                    className="inline-flex items-center gap-2 text-sm text-[#C8C2B7] transition-colors hover:text-[#E6CA65] sm:text-base"
                  >
                    <Youtube className="h-4 w-4" aria-hidden="true" /> YouTube
                  </a>
                </li>
              )}
              <li>
                <a
                  href={withUtm(SITE.bookMyShowUrl)}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="footer-bookmyshow-link"
                  onClick={() => {
                    trackEvent("book_ticket_click", { cta_location: "footer" });
                    trackOutbound("bookmyshow", { cta_location: "footer" });
                  }}
                  className="text-sm text-[#C8C2B7] transition-colors hover:text-[#E6CA65] sm:text-base"
                >
                  BookMyShow
                </a>
              </li>
              <li>
                <a
                  href={`https://wa.me/${SITE.whatsappNumber}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="footer-whatsapp-enquiries-link"
                  onClick={() => {
                    trackEvent("whatsapp_click");
                    trackOutbound("whatsapp-enquiries");
                  }}
                  className="text-sm text-[#C8C2B7] transition-colors hover:text-[#E6CA65] sm:text-base"
                >
                  WhatsApp enquiries: +91 93539 52967
                </a>
              </li>
              <li>
                {SITE.contactEmail ? (
                  <a
                    href={`mailto:${SITE.contactEmail}`}
                    data-testid="footer-contact-link"
                    className="text-sm text-[#C8C2B7] transition-colors hover:text-[#E6CA65] sm:text-base"
                  >
                    {SITE.contactEmail}
                  </a>
                ) : (
                  <span data-testid="footer-contact-placeholder" className="text-sm text-[#918A7E] sm:text-base">
                    Contact details coming soon
                  </span>
                )}
              </li>
            </ul>
          </div>

          <div>
            <p className="text-xs font-bold uppercase tracking-[0.28em] text-[#D4AF37]">Legal</p>
            <ul className="mt-5 space-y-3">
              <li>
                <a
                  href={SITE.privacyUrl}
                  data-testid="footer-privacy-link"
                  className="text-sm text-[#C8C2B7] transition-colors hover:text-[#E6CA65] sm:text-base"
                >
                  Privacy Policy
                </a>
              </li>
              <li>
                <a
                  href={SITE.termsUrl}
                  data-testid="footer-terms-link"
                  className="text-sm text-[#C8C2B7] transition-colors hover:text-[#E6CA65] sm:text-base"
                >
                  Terms &amp; Conditions
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-14 flex flex-col items-center gap-3 border-t border-[#D4AF37]/10 pt-8 text-center sm:flex-row sm:justify-between sm:text-left">
          <p className="text-xs text-[#918A7E] sm:text-sm">
            © {new Date().getFullYear()} {SITE.eventName} · {SITE.city}
          </p>
          <p className="text-xs text-[#918A7E] sm:text-sm">
            Tickets from ₹{SITE.priceFrom} · Official tickets exclusively on BookMyShow
          </p>
        </div>
      </div>
    </footer>
  );
}
